import { useEffect, useRef, useState } from 'react';
import { setupBloomreachCredentials } from '../../api/gsIntg';
import './ConsentAccessSetup.css';

const DEFAULT_URL = 'https://api-engagement.bloomreach.com';

const ERROR_REQUIRED = 'Fill in the API base URL, the project token and the group key.';
const ERROR_HTTPS = 'The API base URL must start with https://.';
const ERROR_FALLBACK = "Couldn't save. Try again.";
// gs-intg's answer when the number already has credentials: finished by someone else meanwhile, or started for another
// project.
const STATUS_CONFLICT = 409;
const HELP =
  'Bloomreach shows these under Project settings › Access management › API. The group key needs permission to read consent categories and to track events.';

// The first thing wrong with the trimmed values, or '' when they can be sent.
const problemWith = ({ url, projectToken, groupKey }) => {
  if (!url || !projectToken || !groupKey) return ERROR_REQUIRED;
  if (!url.startsWith('https://')) return ERROR_HTTPS;
  return '';
};

// gs-intg's display string (an array when it lists several problems), else the generic line.
const messageOf = (error) => {
  const message = error?.response?.data?.message;
  const text = Array.isArray(message) ? message.join(' ') : message;
  return typeof text === 'string' && text.trim() ? text : ERROR_FALLBACK;
};

// A label and its input; the grid in ConsentAccessSetup.css puts them side by side.
const Row = ({ id, label, ...input }) => (
  <>
    <label className="br-sendto__label" htmlFor={id}>
      {label}
    </label>
    <input id={id} className="br-sendto__input" spellCheck={false} autoComplete="off" {...input} />
  </>
);

// The Bloomreach credentials of one sending number, asked for when gs-intg holds none (ConsentControl shows this on a
// 404 only). It renders inside ConsentControl's .br-sendto card, which Message/index.js keeps out of the double-brace
// watch, so none of these inputs reach the composer's brace handling. The group key lives only in this state: nothing is
// logged, and closing the form (Cancel, a save, a new number) unmounts it and drops the key.
// onSaved is a 201. onCredentialsSaved is a 201 or a 409, and it also runs when the form is gone by then: after a 201 the
// keys are on the org, after a 409 the number was set up meanwhile, maybe by someone else. Either way a copy of the org is
// stale, so whoever holds one has to reload it. onConflict is a 409, called once its message is on screen: the parent asks
// for the categories again, and the form stays, with that message and the typed values, for as long as the answer is still
// a 404.
// The project token starts empty: the org id is often not the Bloomreach project token, and a wrong one that looks right
// is worse than an empty field.
const ConsentAccessSetup = ({ orgId, number, onSaved, onCredentialsSaved, onConflict, onCancel }) => {
  const [values, setValues] = useState({
    url: DEFAULT_URL,
    projectToken: '',
    groupKey: '',
  });
  // What the error line shows. A new object per failure, so the focus effect below also runs when the same message returns.
  const [failure, setFailure] = useState(null);
  const [busy, setBusy] = useState(false);
  const errorLine = useRef(null);
  // A save that finishes after the form is gone (the number changed) must not touch the card.
  const mounted = useRef(false);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  // Busy disables the focused button and every input, which drops focus to the page, and nothing hands it back. After a
  // failed check or save the error line takes it, so a keyboard or screen-reader user lands on the message.
  useEffect(() => {
    if (failure) errorLine.current?.focus();
  }, [failure]);

  const change = (name) => (event) => {
    const { value } = event.target;
    setValues((current) => ({ ...current, [name]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    const clean = Object.fromEntries(Object.entries(values).map(([name, value]) => [name, value.trim()]));
    const problem = problemWith(clean);
    setFailure(problem ? { text: problem } : null);
    if (problem) return;

    const body = { number, ...clean };
    setBusy(true);
    try {
      await setupBloomreachCredentials(orgId, body);
    } catch (err) {
      const conflict = err?.response?.status === STATUS_CONFLICT;
      if (mounted.current) {
        setFailure({ text: messageOf(err) });
        setBusy(false);
        if (conflict) onConflict();
      }
      // Outside the guard: the org copy is stale whether or not the form is still on screen.
      if (conflict) onCredentialsSaved?.();
      return;
    }
    // Saved. The org holds the keys now whether or not this form is still on screen.
    onCredentialsSaved?.();
    if (mounted.current) onSaved();
  };

  return (
    <form className="br-consent-setup" data-testid="bloomreach-consent-setup" noValidate onSubmit={submit}>
      <Row
        id="br-consent-setup-url"
        label="API base URL"
        data-testid="bloomreach-consent-setup-url"
        type="url"
        required
        disabled={busy}
        value={values.url}
        onChange={change('url')}
      />
      <Row
        id="br-consent-setup-project-token"
        label="Project token"
        data-testid="bloomreach-consent-setup-project-token"
        type="text"
        required
        // The base URL is prefilled and the token never is, so the token is the first field to fill.
        autoFocus
        disabled={busy}
        value={values.projectToken}
        onChange={change('projectToken')}
      />
      <Row
        id="br-consent-setup-group-key"
        label="Group key"
        data-testid="bloomreach-consent-setup-group-key"
        type="password"
        autoComplete="new-password"
        required
        disabled={busy}
        value={values.groupKey}
        onChange={change('groupKey')}
      />
      <p className="br-sendto__hint br-consent-setup__wide">{HELP}</p>
      {failure && (
        <p
          ref={errorLine}
          tabIndex={-1}
          className="br-sendto__error br-consent-setup__wide"
          role="alert"
          data-testid="bloomreach-consent-setup-error"
        >
          {failure.text}
        </p>
      )}
      <div className="br-consent-setup__actions">
        <button type="submit" className="br-consent-setup__submit" data-testid="bloomreach-consent-setup-submit" disabled={busy}>
          {busy ? 'Checking…' : 'Check and save'}
        </button>
        <button
          type="button"
          className="br-sendto__reset"
          data-testid="bloomreach-consent-setup-cancel"
          disabled={busy}
          onClick={onCancel}
        >
          Cancel
        </button>
      </div>
    </form>
  );
};

export default ConsentAccessSetup;
